import type { MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { Trade } from '../types/trade'

function formatDate(dateString: string | null) {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatPriceWithCurrency(price: number | null, currency: string | null) {
  if (price === null) return '-'
  const formatted = price.toFixed(2).replace('.', ',')
  return currency ? `${formatted} ${currency}` : formatted
}

function formatCurrency(amount: number | null, currency: string | null) {
  if (amount === null) return '-'
  const formatted = amount.toFixed(2).replace('.', ',')
  return currency ? `${formatted} ${currency}` : formatted
}

function isTradeOpen(trade: Trade) {
  return !trade.exit_date
}

export default function TradeQuickReviewModal({
  trade,
  appearance = 'classic',
  onClose,
  onViewFullDetail,
  onEditTrade,
}: {
  trade: Trade
  appearance?: 'classic' | 'studio'
  onClose: () => void
  onViewFullDetail: (trade: Trade, event: MouseEvent<HTMLElement>) => void
  onEditTrade?: (trade: Trade) => void
}) {
  const { t } = useTranslation(['trading', 'common'])
  const studio = appearance === 'studio'
  const open = isTradeOpen(trade)

  return (
    <div
      className={studio ? 'at-quick-trade-backdrop' : 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200] p-2 sm:p-4'}
      onClick={onClose}
    >
      <div
        className={studio ? 'at-trade-quick-review' : 'rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-xl animate-in fade-in zoom-in duration-200'}
        style={studio ? undefined : { backgroundColor: '#FBFBFB' }}
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className={studio ? 'at-trade-quick-review-head' : 'flex-shrink-0 border-b border-gray-200 px-4 md:px-6 py-3 flex justify-between items-start gap-2 z-10'}
          style={studio ? undefined : { backgroundColor: '#FBFBFB' }}
        >
          <div className="min-w-0 flex-1">
            <h2 className={studio ? undefined : 'text-lg md:text-xl font-semibold text-gray-900 truncate'}>{trade.symbol}</h2>
            {trade.strategy && (
              <p className={studio ? 'at-trade-quick-strategy' : 'text-xs md:text-sm text-gray-600 truncate mt-0.5'}>{trade.strategy}</p>
            )}
            <div className="at-trade-quick-badges">
              <span className={`at-side ${trade.long_short === 'short' ? 'short' : ''}`}>{trade.long_short || 'long'}</span>
              <span className={`at-status ${open ? 'open' : ''}`}>{open ? 'Open' : 'Closed'}</span>
            </div>
            <button
              type="button"
              className="at-trade-quick-link"
              onClick={(event) => onViewFullDetail(trade, event)}
            >
              View full details →
            </button>
          </div>
          <button type="button" className="at-trade-quick-close" onClick={onClose} aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={studio ? 'at-trade-quick-review-body' : 'flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 md:p-5'}>
          <div className="space-y-4">
            {open && trade.current_price && (
              <div className={studio ? 'at-trade-quick-panel' : 'bg-blue-50 p-3 md:p-4 rounded-xl border border-blue-100'}>
                <h3 className="at-trade-quick-section-title">Current market data</h3>
                <div className="at-trade-quick-grid">
                  <div>
                    <span className="at-trade-quick-label">Current price</span>
                    <div className="at-trade-quick-value">{formatPriceWithCurrency(trade.current_price, trade.currency)}</div>
                  </div>
                  <div>
                    <span className="at-trade-quick-label">Daily change</span>
                    <div className={`at-trade-quick-value ${trade.daily_change_percentage != null && trade.daily_change_percentage >= 0 ? 'positive' : 'negative'}`}>
                      {trade.daily_change_percentage != null
                        ? `${trade.daily_change_percentage >= 0 ? '+' : ''}${trade.daily_change_percentage.toFixed(2).replace('.', ',')}%`
                        : '-'}
                    </div>
                  </div>
                  <div>
                    <span className="at-trade-quick-label">Unrealized P/L</span>
                    <div className={`at-trade-quick-value ${trade.unrealized_pnl != null && trade.unrealized_pnl >= 0 ? 'positive' : 'negative'}`}>
                      {trade.unrealized_pnl != null ? formatCurrency(trade.unrealized_pnl, trade.currency) : '-'}
                    </div>
                  </div>
                  <div>
                    <span className="at-trade-quick-label">Unrealized P/L %</span>
                    <div className={`at-trade-quick-value ${trade.unrealized_pnl_percentage != null && trade.unrealized_pnl_percentage >= 0 ? 'positive' : 'negative'}`}>
                      {trade.unrealized_pnl_percentage != null
                        ? `${trade.unrealized_pnl_percentage >= 0 ? '+' : ''}${trade.unrealized_pnl_percentage.toFixed(2).replace('.', ',')}%`
                        : '-'}
                    </div>
                  </div>
                </div>
                {trade.current_price_updated_at && (
                  <p className="at-trade-quick-muted">Last updated: {formatDate(trade.current_price_updated_at)}</p>
                )}
              </div>
            )}

            <div className="at-trade-quick-grid">
              <div>
                <span className="at-trade-quick-label">Entry date</span>
                <div className="at-trade-quick-value">{formatDate(trade.entry_date)}</div>
              </div>
              <div>
                <span className="at-trade-quick-label">Entry price</span>
                <div className="at-trade-quick-value">{formatPriceWithCurrency(trade.entry_price, trade.currency)}</div>
              </div>
              <div>
                <span className="at-trade-quick-label">Stop loss</span>
                <div className="at-trade-quick-value">{formatPriceWithCurrency(trade.stop_loss, trade.currency)}</div>
              </div>
              <div>
                <span className="at-trade-quick-label">Take profit</span>
                <div className="at-trade-quick-value">{formatPriceWithCurrency(trade.take_profit, trade.currency)}</div>
              </div>
            </div>

            <div className="at-trade-quick-grid">
              {trade.quantity && (
                <div>
                  <span className="at-trade-quick-label">Quantity</span>
                  <div className="at-trade-quick-value">{trade.quantity}</div>
                </div>
              )}
              {trade.trade_type !== 'spot' && trade.contract_size && (
                <div>
                  <span className="at-trade-quick-label">Contract size</span>
                  <div className="at-trade-quick-value">{trade.contract_size}</div>
                </div>
              )}
              {trade.position_size && (
                <div>
                  <span className="at-trade-quick-label">Position size</span>
                  <div className="at-trade-quick-value">{formatCurrency(trade.position_size, trade.currency)}</div>
                </div>
              )}
              <div>
                <span className="at-trade-quick-label">Planned R:R</span>
                <div className="at-trade-quick-value">{trade.rr != null ? trade.rr.toFixed(2) : '-'}</div>
              </div>
              <div>
                <span className="at-trade-quick-label">Currency</span>
                <div className="at-trade-quick-value">{trade.currency || '-'}</div>
              </div>
            </div>

            {!open && (
              <div className={studio ? 'at-trade-quick-panel' : 'bg-orange-50 p-3 md:p-4 rounded-xl border border-orange-100'}>
                <h3 className="at-trade-quick-section-title">Exit results</h3>
                <div className="at-trade-quick-grid">
                  {trade.exit_date && (
                    <div>
                      <span className="at-trade-quick-label">Exit date</span>
                      <div className="at-trade-quick-value">{formatDate(trade.exit_date)}</div>
                    </div>
                  )}
                  {trade.exit_price && (
                    <div>
                      <span className="at-trade-quick-label">Exit price</span>
                      <div className="at-trade-quick-value">{formatPriceWithCurrency(trade.exit_price, trade.currency)}</div>
                    </div>
                  )}
                  {trade.pnl !== null && (
                    <div>
                      <span className="at-trade-quick-label">P/L</span>
                      <div className={`at-trade-quick-value ${trade.pnl >= 0 ? 'positive' : 'negative'}`}>
                        {formatCurrency(trade.pnl, trade.currency)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {trade.trade_setup && (
              <div>
                <span className="at-trade-quick-label">Setup</span>
                <div className="at-trade-quick-value">{trade.trade_setup}</div>
              </div>
            )}

            {trade.notes && (
              <div className="at-trade-quick-notes">
                <span className="at-trade-quick-label">Journal notes</span>
                <p>{trade.notes}</p>
              </div>
            )}
          </div>
        </div>

        <div className={studio ? 'at-trade-quick-review-foot' : 'flex-shrink-0 flex flex-col sm:flex-row gap-2 sm:gap-3 p-4 md:p-5 border-t border-gray-200 rounded-b-2xl'} style={studio ? undefined : { backgroundColor: '#FBFBFB' }}>
          <button
            type="button"
            className="at-trade-quick-primary"
            onClick={() => onEditTrade?.(trade)}
            disabled={!onEditTrade}
          >
            {t('tradeList.editTrade', { defaultValue: 'Edit trade' })}
          </button>
          <button type="button" className="at-trade-quick-secondary" onClick={onClose}>
            {t('buttons.close', { ns: 'common', defaultValue: 'Close' })}
          </button>
        </div>
      </div>
    </div>
  )
}
